import { DatePicker, ErrorMessage } from '@/components';
import type { CSSProperties } from 'react';
import {
  Controller,
  type FieldErrors,
  type FieldValues,
  type Path,
} from 'react-hook-form';
import { Form } from '.';
import { FieldLabel, type FieldLabelSize } from './FieldLabel';
import type { FormControlProp, RangePickerProps } from './types';

type Props<T extends FieldValues> = {
  name: Path<T>;
  label: string;
  control: FormControlProp<T>;
  errors: FieldErrors;
  required?: boolean;
  disabled?: boolean;
  picker?: 'date' | 'week' | 'month' | 'quarter' | 'year';
  mode?: 'time' | 'date' | 'week' | 'month' | 'quarter' | 'year' | 'decade';
  hint?: string;
  placeholder?: [string, string];
  allowEmpty?: [boolean, boolean];
  wrapperStyle?: CSSProperties;
  fieldStyle?: CSSProperties;
  shouldCoexistOnChange?: boolean;
  hiddenErrorMessage?: boolean;
  labelSize?: FieldLabelSize;
  labelStyle?: CSSProperties;
} & Pick<
  RangePickerProps,
  'onChange' | 'disabledDate' | 'allowClear' | 'minDate' | 'maxDate' | 'format'
>;

export const DateRangePickerField = <T extends FieldValues>({
  name,
  label,
  control,
  errors,
  required,
  disabled,
  picker,
  mode,
  hint,
  placeholder = ['', ''],
  allowEmpty,
  wrapperStyle,
  fieldStyle,
  shouldCoexistOnChange = false,
  onChange,
  hiddenErrorMessage = false,
  labelSize = 'medium',
  disabledDate,
  allowClear,
  minDate,
  maxDate,
  format,
  labelStyle,
}: Props<T>) => {
  return (
    <Form.Item
      style={wrapperStyle}
      label={
        label && (
          <FieldLabel
            text={label}
            required={required}
            hint={hint}
            size={labelSize}
            style={labelStyle}
          />
        )
      }
    >
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <DatePicker.RangePicker
            picker={picker}
            mode={mode ? [mode, mode] : undefined}
            placeholder={placeholder}
            allowEmpty={allowEmpty}
            disabled={disabled}
            disabledDate={disabledDate}
            {...field}
            allowClear={allowClear}
            style={fieldStyle}
            minDate={minDate}
            maxDate={maxDate}
            format={format}
            onChange={(values, formatString) => {
              if (picker === 'month') {
                values?.splice(
                  0,
                  2,
                  values[0]?.startOf(picker) ?? null,
                  values[1]?.endOf(picker) ?? null,
                );
              }

              if (shouldCoexistOnChange || !onChange) {
                field.onChange(values, formatString);
              }
              onChange?.(values, formatString);
            }}
            data-dd-privacy="allow"
          />
        )}
      />
      {!hiddenErrorMessage && <ErrorMessage errors={errors} name={name} />}
    </Form.Item>
  );
};
